/* eslint-disable no-underscore-dangle */
import * as Sentry from '@sentry/browser';
import axios, {
    AxiosError,
    AxiosInstance,
    AxiosPromise,
    AxiosRequestConfig,
    AxiosResponse,
    CancelTokenStatic,
} from 'axios';
import { LOGIN_URL } from '@synergy/config';

import { ApiResult, errorResult, successResult } from './models/api-result';
import { HandledError } from './models/handled-error';
import { PaginatedResponse } from './models/paginated-response';
import { RequestError } from './models/request-error';

// Reexport canceler to avoid importing from multiple different packages
export type { Canceler } from 'axios';

interface RequestType extends AxiosInstance {
    CancelToken: CancelTokenStatic;
    isCancel: (thrown: unknown) => boolean;
    setRedirectOnAuthError: (redirect: boolean) => void;
}

interface RequestConfig extends AxiosRequestConfig {
    redirectOnAuthError?: boolean;
}
// Default config
const config: RequestConfig = {
    redirectOnAuthError: true,
    withCredentials: true,
};
export const request = axios.create(config) as RequestType;

request.CancelToken = axios.CancelToken;
request.isCancel = axios.isCancel;
request.setRedirectOnAuthError = (redirect: boolean) => {
    // TODO: Find correct typings for this
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (request.defaults as any).redirectOnAuthError = redirect;
};
request.interceptors.response.use(
    response => {
        if (response?.data) {
            // remap paginated response
            if (!preProcessPaginatedData(response.data)) {
                // It could be a collection of paginated responses
                Object.keys(response.data).forEach(key => {
                    preProcessPaginatedData(response.data[key]);
                });
            }
        }
        return response;
    },
    error => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            if ((error.response.config as RequestConfig)?.redirectOnAuthError) {
                window.location.replace(
                    `${LOGIN_URL}?redirect_url=${encodeURI(window.location.href)}&status=${encodeURI(
                        error.response.status
                    )}`
                );
                return;
            }
        }

        throw error;
    }
);

const preProcessPaginatedData = <T>(data?: PaginatedResponse<T>): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyData = data as any;

    // Always remap _meta to meta
    if (anyData?._meta) {
        anyData.meta = anyData._meta;
        delete anyData._meta;
    }

    // try to remap _embedded to items. If the response is a collection of collections, this fails and we return false.
    if (anyData?._embedded) {
        anyData.items = anyData._embedded;
        delete anyData._embedded;
        return true;
    }
    return false;
};

export const safeRequest = async <TResponse, TRequest = TResponse, TError = string>(
    call: () => AxiosPromise<TRequest>,
    getData: (response: AxiosResponse<TRequest>) => TResponse,
    getErrors?: (error?: RequestError) => HandledError<TError>[]
): Promise<ApiResult<TResponse>> => {
    try {
        const response = await call();
        if (!response) {
            return errorResult();
        }
        const data = getData(response);
        return successResult(data);
    } catch (error) {
        const axiosError = error as AxiosError;
        if (process.env['NODE_ENV'] !== 'production') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-console
            console.error(`safeRequest error: ${(axiosError?.response?.data as any)?.error ?? ''}\n`, error);
        }
        const handledErrors = getErrors?.(axiosError?.response?.data as RequestError);
        if (
            (!handledErrors || handledErrors.length === 0) &&
            axiosError.response?.status !== 401 &&
            axiosError.response?.status !== 403 &&
            axiosError.code !== 'ERR_CANCELED'
        ) {
            Sentry.captureException(error);
        }
        return errorResult<TError>(axiosError, handledErrors);
    }
};
